import { useState } from "react";
import {
  QueryObserverResult,
  RefetchOptions,
  useMutation
} from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Application } from "types/application";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import {
  ApplicantStatus,
  ApplicationStatus,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { Clock, CursorClick01 } from "untitledui-js-base";
import { useMakeDecisionController } from "shared/app/MakeDecisionModal/controller";
import { ResumeTooltip } from "shared/app/OnHoldResume/ResumeTooltip";
import { useAuthContext } from "providers/Auth";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { RunReportConfirmModal } from "./RunReportConfirmModal";

const CREDIT_REPORT_COMPLETED_STATUSES = [
  VerificationStatus.Finished,
  VerificationStatus.FinishedWithWarnings,
  VerificationStatus.FinishedByOverridden,
  VerificationStatus.Verified
];

export const ManualReportView = ({
  applicantId,
  applicationId,
  verificationId,
  applicantStatus,
  applicationStatus,
  verificationName,
  creditReportStatus,
  refetch
}: {
  applicantId: number;
  applicationId: number;
  verificationId: number;
  applicantStatus?: ApplicantStatus;
  applicationStatus?: ApplicationStatus;
  verificationName?: VerificationNames;
  creditReportStatus?: VerificationStatus;
  refetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<Application | undefined, unknown>>;
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { permissions } = useAuthContext();
  const { applicationMeta } = useMakeDecisionController(String(applicationId));

  const needsCreditReportWarning =
    verificationName === VerificationNames.EvictionReport &&
    creditReportStatus !== undefined &&
    !CREDIT_REPORT_COMPLETED_STATUSES.includes(creditReportStatus);

  const { mutate, isPending } = useMutation({
    mutationKey: ["VERIFICATION", applicationId, verificationId, "RUN_REPORT"],
    mutationFn: () => {
      return instance.post(
        `/portal/v1/applications/${applicationId}/applicants/${applicantId}/verifications/${verificationId}/run_report`
      );
    },
    onSuccess: () => {
      //refetch for report page
      refetch();
      //refetch for make decision modal
      applicationMeta.refetch();
      setShowConfirmModal(false);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const handleRunReport = () => {
    if (needsCreditReportWarning) setShowConfirmModal(true);
    else mutate();
  };

  if (applicantStatus === ApplicantStatus.OnHold)
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="flex max-w-md flex-col items-center justify-center gap-6 p-6">
          <div className="rounded-full bg-alpha-20 p-5">
            <Clock size="20px" />
          </div>
          <p className="text-center text-sm font-medium text-content-300">
            This applicant is currently on hold. Please resume the application
            to run the report.
          </p>
        </div>
      </div>
    );

  if (applicantStatus !== ApplicantStatus.Submitted)
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="flex max-w-md flex-col items-center justify-center gap-6 p-6">
          <div className="rounded-full bg-alpha-20 p-5">
            <Clock size="20px" />
          </div>
          <p className="text-center text-sm font-medium text-content-300">
            This report will be available to run manually after the applicant
            completes the application
          </p>
        </div>
      </div>
    );

  return (
    <>
      <div className="flex w-full flex-col items-center pt-16">
        <div className="flex max-w-md flex-col items-center gap-6 p-6">
          <div className="rounded-full bg-primary-20 p-5">
            <CursorClick01 size="24px" className="stroke-primary-500" />
          </div>

          <div className="space-y-2">
            <p className="text-center text-base font-bold text-content-500">
              This report was configured to run manually
            </p>

            <p className="text-center text-sm font-medium text-content-300">
              {permissions.canRunReportsManually
                ? "Please click the button below to run the report"
                : " This report was configured to run manually, but you do not have permission to run it. Please contact your administrator if you believe this is an error"}
            </p>
          </div>

          <Button
            size={ButtonSize.BASE}
            variant={ButtonVariant.PRIMARY}
            onClick={handleRunReport}
            loading={isPending}
            disabled={
              applicationStatus === ApplicationStatus.OnHold ||
              !permissions.canRunReportsManually
            }
            tooltipDelay={250}
            tooltip={
              applicationStatus === ApplicationStatus.OnHold ? (
                <ResumeTooltip applicationId={applicationId} />
              ) : undefined
            }
          >
            Run report
          </Button>
        </div>
      </div>

      <RunReportConfirmModal
        opened={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => mutate()}
        isLoading={isPending}
      />
    </>
  );
};
